(() => {
  if (customElements.get('product-form')) {
    return;
  }

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.form.querySelector('[name="add"]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productBar = document.querySelector(
        `#ProductBar-${this.dataset.sectionId}`
      );
      this.quantityInput = this.form.querySelector('[name="quantity"]');
      
      this.currentVariantPrice = null;
      this.isInitializing = true;

      // Get fallback price from variant
      this.getFallbackPrice();

      this.form.addEventListener(
        'submit',
        this.onSubmitHandler.bind(this)
      );

      // Listen for quantity changes
      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', this.updateButtonPrice.bind(this));
      }

      // Listen for variant changes
      document.addEventListener('variant:change', this.handleVariantChange.bind(this));
      
      // Initialize Loop subscription detection
      this.initLoopSubscription();
      
      // Block Loop from updating button text
      this.blockLoopButtonUpdates();
      
      // Mark initialization complete after a short delay
      setTimeout(() => {
        this.isInitializing = false;
        if (this.currentVariantPrice) {
          this.updateButtonPrice();
        }
      }, 300);
    }

    initLoopSubscription(retryCount = 0) {
      const maxRetries = 20;
      const loopContainer = this.getLoopWidgetContainer();
      
      if (!loopContainer) {
        if (retryCount < maxRetries) {
          setTimeout(() => this.initLoopSubscription(retryCount + 1), 300);
          return;
        }
        return;
      }

      // Loop widget found - update price and setup listeners
      this.updatePriceFromLoop();
      this.setupLoopObserver(loopContainer);
      this.setupLoopClickHandler(loopContainer);
    }
    
    updatePriceFromLoop() {
      const loopPrice = this.getLoopSubscriptionPrice();
      if (loopPrice) {
        this.currentVariantPrice = loopPrice;
        this.updateButtonPrice();
      }
    }
    
    setupLoopClickHandler(loopContainer) {
      loopContainer.addEventListener('click', (e) => {
        const purchaseOption = e.target.closest('.loop-widget-purchase-option');
        if (purchaseOption) {
          const priceElem = purchaseOption.querySelector('.loop-widget-purchase-option-price');
          if (priceElem) {
            const priceText = priceElem.textContent.trim();
            const priceMatch = priceText.match(/[\d,]+\.?\d*/);
            if (priceMatch) {
              const price = parseFloat(priceMatch[0].replace(/,/g, '')) * 100;
              this.currentVariantPrice = price;
              this.updateButtonPrice();
            }
          }
        }
      });
    }

    getLoopWidgetContainer() {
      return document.querySelector('.loop-widget-purchase-options-container')
        || document.querySelector('.loop-widget-purchase-options')
        || document.querySelector('.loop-widget');
    }

    getLoopSubscriptionPrice() {
      const loopContainer = document.querySelector('.loop-widget-purchase-options-container');
      if (!loopContainer) return null;
      
      let selected = loopContainer.querySelector('.loop-widget-purchase-option-selected');
      if (!selected) {
        selected = loopContainer.querySelector('[aria-checked="true"]');
      }
      
      if (!selected) return null;
      
      const isOneTime = selected.classList.contains('loop-widget-purchase-option-onetime');
      if (isOneTime) return null;
      
      const priceElem = selected.querySelector('.loop-widget-purchase-option-price');
      if (!priceElem) return null;
      
      const priceText = priceElem.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (!priceMatch) return null;
      
      const price = parseFloat(priceMatch[0].replace(/,/g, ''));
      return price * 100;
    }

    getFallbackPrice() {
      if (this.dataset.sectionId) {
        const productSelector = document.querySelector(`product-selector[data-section-id="${this.dataset.sectionId}"]`);
        if (productSelector && productSelector.currentVariant && productSelector.currentVariant.price) {
          this.currentVariantPrice = productSelector.currentVariant.price;
        }
      }
    }
    
    setupLoopObserver(container) {
      const observer = new MutationObserver(() => {
        setTimeout(() => this.updatePriceFromLoop(), 100);
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
        characterData: true
      });
    }


    onSubmitHandler(event) {
      event.preventDefault();
      const purchaseOptions = document.querySelector(
        'purchase-options'
      );
      if (
        !!purchaseOptions &&
        !purchaseOptions.isOneTimePurchase() &&
        !this.hasAttribute('data-has-selling-plan')
      ) {
        purchaseOptions.showError();
        return;
      }

      if (this.isAddingToCart) return; // guard against double-tap double-add
      this.isAddingToCart = true;
      this.submitButton.classList.add('disabled');
      this.submitButton.setAttribute('disabled', 'disabled');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(this.form);
      formData.append(
        'sections',
        this.cartDrawer
          .getSectionsToRender()
          .map(section => section.section)
      );
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          this.cartDrawer.renderContents(response);
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.isAddingToCart = false;
          this.submitButton.classList.remove('disabled');
          this.submitButton.removeAttribute('disabled');
        });
    }

    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper) return;

      let message = errorMessage;

      if (typeof errorMessage === 'object' && errorMessage !== null) {
        message = Object.values(errorMessage).flat().join(', ');
      }

      errorWrapper.classList.toggle('hidden', !message);
      errorWrapper.textContent = message || '';
    }


    toggleAddButton(disable, text) {
      [
        this.submitButton,
        this.productBar?.querySelector('[name="add"]')
      ].forEach(submitButton => {
        if (disable) {
          submitButton?.setAttribute('disabled', 'disabled');
          if (text && submitButton) submitButton.textContent = text;
        } else if (submitButton) {
          submitButton.removeAttribute('disabled');
          this.updateButtonPrice();
        }
      });
    }

    handleVariantChange(event) {
      if (event.detail && event.detail.variant) {
        const loopPrice = this.getLoopSubscriptionPrice();
        this.currentVariantPrice = loopPrice || event.detail.variant.price;
        this.updateButtonPrice();
      }
    }

    updateButtonPrice() {
      if (!this.submitButton || !this.currentVariantPrice) return;
      if (this.isInitializing) return;

      const quantity = this.quantityInput ? parseInt(this.quantityInput.value) || 1 : 1;
      const totalPrice = this.currentVariantPrice * quantity;
      
      const baseText = this.submitButton.getAttribute('data-button-text') || 
                       window.variantStrings.addToCart || 
                       'Add to cart';
      
      const formattedPrice = this.formatMoney(totalPrice);
      const buttonText = `${baseText} - ${formattedPrice}`;
      this.submitButton.textContent = buttonText;
    }

    formatMoney(cents) {
      // Use Shopify's formatMoney if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      
      // Fallback: Use Intl.NumberFormat with currency
      const currency = window.Shopify?.currency?.active || 'USD';
      const price = cents / 100;
      
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(price);
    }

    blockLoopButtonUpdates() {
      if (!this.submitButton) return;
      
      // Flag to track when we're updating to avoid blocking ourselves
      this._isOurUpdate = false;
      let lastGoodText = this.submitButton.textContent;
      
      const self = this;
      
      // Watch for text changes via MutationObserver
      const observer = new MutationObserver((mutations) => {
        // Skip if we're in the middle of our own update
        if (self._isOurUpdate) return;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const currentText = self.submitButton.textContent;
            
            // Check if the text has our price format (with " - " before currency)
            const hasOurPriceFormat = / -\s*[\$£€]/i.test(currentText);
            
            // If Loop changed the text and it doesn't have our format, restore ours
            if (!hasOurPriceFormat && currentText !== lastGoodText && self.currentVariantPrice && !self.isInitializing) {
              // Debounce to avoid rapid-fire updates
              clearTimeout(self._loopBlockTimeout);
              self._loopBlockTimeout = setTimeout(() => {
                self._isOurUpdate = true;
                self.updateButtonPrice();
                setTimeout(() => {
                  self._isOurUpdate = false;
                }, 50);
              }, 10);
            } else if (hasOurPriceFormat) {
              // This is our format, remember it as good
              lastGoodText = currentText;
            }
          }
        });
      });
      
      observer.observe(this.submitButton, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  customElements.define('product-form', ProductForm);
})();
